import { createListenerMiddleware, PayloadAction } from '@reduxjs/toolkit';

import type { PlayerConfigBodyType } from '../../api/fetchers/playerConfigSync/types';
import { fetchPlayerConfigSyncAction } from '../apiActions/fetchPlayerConfigSyncAction';
import type { CoreRootState } from '../CoreStore';
import { chipActions } from '../slices/chips/slice';
import { ChipTypes, FreeChipValueType } from '../slices/chips/types';
import { settingsActions } from '../slices/settings/slice';

type SyncActionType =
  | PayloadAction<boolean, typeof settingsActions.setStreamEnabled.type>
  | PayloadAction<{ channel: string | number; value: number }, typeof settingsActions.setVolume.type>
  | PayloadAction<number, typeof settingsActions.setMasterVolume.type>
  | PayloadAction<boolean, typeof settingsActions.setShowOtherMessages.type>
  | PayloadAction<{ weight: number }, typeof chipActions.setAndSyncSelectedChip.type>
  | PayloadAction<FreeChipValueType[], typeof chipActions.setFreeChips.type>;

export const settingSyncMiddleware = createListenerMiddleware<CoreRootState>();

// List of actions that trigger this middleware to send POST messages to the server
const actionCreators = [
  settingsActions.setStreamEnabled,
  settingsActions.setVolume,
  settingsActions.setMasterVolume,
  settingsActions.setShowOtherMessages,
  chipActions.setAndSyncSelectedChip,
  chipActions.setFreeChips,
];

actionCreators.forEach((actionCreator) => {
  settingSyncMiddleware.startListening({
    actionCreator,
    effect: (action, { dispatch, getState }) => {
      const state = getState();
      const session = state.config.session;
      const freeChips = state.chips.chipsData.freeChips;
      const selectedChipFb: string = state.config.playerConfig?.freeChipsParamsRaw?.selected_chip_fb ?? '';

      if (action.type === chipActions.setFreeChips.type) {
        if (freeChips.length > 0 || selectedChipFb === '') return;
      }
      if (!session) return;
      const { JSESSIONID, metaServer: url, gametype } = session;
      if (!(JSESSIONID && url)) return;

      const body = generateBody(action, getState());
      const timeStamp = Date.now();

      dispatch(
        fetchPlayerConfigSyncAction({
          JSESSIONID,
          ck: timeStamp.toString(),
          url: `${url}/api/ui/savePlayerConfigGeneric`,
          body: { ...body, gameType: gametype ?? '' },
        })
      );
    },
  });
});

const generateBody = (action: SyncActionType, state: CoreRootState): Record<string, unknown> => {
  const { type, payload } = action;
  const gameId = state.config.tableConfig?.operatorGameId;

  switch (type) {
    case settingsActions.setStreamEnabled.type: {
      if (!gameId) return {};

      // Server enables video stream for value `false` and disables for `true`.
      // Preserve inverse logic here to avoid breaking existing player configs.
      const streamValue = payload ? 'false' : 'true';
      const streamSettingsList: string = state.config.playerConfig?.videoStreamRaw?.video_stream ?? '';

      return { video_stream: updateStreamSettingsList(gameId, streamValue, streamSettingsList) };
    }

    case settingsActions.setVolume.type: {
      const { channel, value } = payload;
      const newVolume = Math.ceil(value * 100).toString();
      const key = getPlayerConfigParamByChannel(channel); // todo: refactor to non-game-specific solution
      // todo: dealer_voice

      return { [key]: newVolume };
    }

    // Save channel volumes on master changes
    case settingsActions.setMasterVolume.type: {
      // todo: see if possible to utilise setVolume action instead, as this makes requests on every master volume change
      // Default to muted (all volumes at 0)
      const volumeBody: Record<string, string> = {
        video_sounds: "0",
        game_effects: "0",
      };

      // Add optional channels with default muted state
      if (state.settings.volume.channels.voiceover) {
        volumeBody.vo_music = "0";
      }
      if (state.settings.volume.channels.background) {
        volumeBody.bg_music = "0";
      }

      // If payload exists, set actual volume values
      if (payload) {
        volumeBody.video_sounds = Math.ceil((state.settings.volume.channels.studio?.value ?? 1) * 100).toString();
        volumeBody.game_effects = Math.ceil((state.settings.volume.channels.game?.value ?? 1) * 100).toString();
        if (state.settings.volume.channels.voiceover) {
          volumeBody.vo_music = Math.ceil(state.settings.volume.channels.voiceover.value * 100).toString();
        }
        if (state.settings.volume.channels.background) {
          volumeBody.bg_music = Math.ceil(state.settings.volume.channels.background.value * 100).toString();
        }
      }

      return volumeBody;
    }

    case chipActions.setAndSyncSelectedChip.type: {
      if (!gameId) return {};

      const { weight } = payload;
      const selectedChipList: string = state.config.playerConfig?.selectedChipParamsRaw?.selected_chip ?? '';
      const updatedSelectedChipList = updateSelectedChipList(gameId, weight, selectedChipList);
      switch (state.chips.selectedChip?.type) {
        case ChipTypes.FreeChip:
          return { selected_chip_fb: gameId };
        case ChipTypes.CasinoChip:
          return { selected_chip: updatedSelectedChipList, selected_chip_fb: '' };
        default:
          return { selected_chip: updatedSelectedChipList, selected_chip_fb: '' };
      }
    }
    case chipActions.setFreeChips.type: {
      return { selected_chip_fb: '' };
    }
    case settingsActions.setShowOtherMessages.type: {
      const chatValue = payload;

      return { showothermessages: chatValue };
    }
    default:
      return {};
  }
};

// Utils
export function updateSelectedChipList(
  gameId: string,
  weight: number,
  selectedChipList: string
): PlayerConfigBodyType['selected_chip'] {
  // Early exit if gameId or weight is not valid
  if (!gameId || weight == null) {
    return selectedChipList;
  }

  const newWeight = weight - 1; // Server expects chip index
  const newSelectedChip = `${gameId}=${newWeight}`;
  const validEntries = sanitizeList(selectedChipList); // Sanitize the list

  // If the gameId already exists in the list, update it; otherwise, append it
  if (validEntries.includes(gameId)) {
    const regex = new RegExp(`${gameId}=\\d+`);
    const oldSelectedChip = validEntries.match(regex);
    return oldSelectedChip ? validEntries.replace(oldSelectedChip[0], newSelectedChip) : validEntries;
  }

  return validEntries ? `${validEntries},${newSelectedChip}` : newSelectedChip;
}

export function updateStreamSettingsList(
  gameId: string,
  streamValue: 'true' | 'false',
  streamSettingsList: string
): PlayerConfigBodyType['video_stream'] {
  const newStreamValue = `${gameId}=${streamValue}`;

  if (streamSettingsList.includes(gameId)) {
    const oldStreamValue = streamSettingsList.match(new RegExp(`${gameId}=(true|false)`));

    if (oldStreamValue) {
      return streamSettingsList.replace(oldStreamValue[0], newStreamValue);
    }
  } else {
    return streamSettingsList ? `${streamSettingsList},${newStreamValue}` : newStreamValue;
  }

  return streamSettingsList;
}

// Function to sanitize the list
function sanitizeList(selectedChipList: string): string {
  return selectedChipList
    .replace(/^,+|,+$/g, '')
    .replace(/,+/g, ',')
    .replace(/\s+/g, '')
    .split(',')
    .filter((entry) => entry.includes('='))
    .join(',');
}

function getPlayerConfigParamByChannel(channel: string | number): string {
  switch (channel) {
    case 'studio':
      return 'video_sounds';
    case 'game':
      return 'game_effects';
    case 'voiceover':
      return 'vo_music';
    case 'background':
      return 'bg_music';
    default:
      return channel as string;
  }
}
